import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr, todayISO, formatDate } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, BookOpen, Receipt, Landmark, ClipboardList, Eye, Printer, CalendarDays } from "lucide-react";

export default function CustomerAccounts() {
  const [patients, setPatients] = useState([]);
  const [allCreditInvoices, setAllCreditInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Detail View Drawer / Dialog
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [statementOpen, setStatementOpen] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [statementFromDate, setStatementFromDate] = useState("");
  const [statementToDate, setStatementToDate] = useState("");

  // Receive Payment Modal
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentPatient, setPaymentPatient] = useState(null);
  const [paymentInvoices, setPaymentInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const today = todayISO();
  const isOverdueInvoice = (invoice) =>
    invoice?.outstanding_amount > 0 && invoice?.due_date && invoice.due_date < today;

  const invoiceDateInRange = (date) => {
    if (!date) return true;
    if (statementFromDate && date < statementFromDate) return false;
    if (statementToDate && date > statementToDate) return false;
    return true;
  };

  const filteredInvoices = invoices.filter((inv) => invoiceDateInRange(inv.credit_date));
  const filteredLedger = ledger.filter((entry) => invoiceDateInRange(entry.date));

  const summary = allCreditInvoices.reduce(
    (acc, inv) => {
      acc.totalCredit += Number(inv.total_amount || 0);
      acc.totalPaid += Number(inv.paid_amount || 0);
      acc.totalOutstanding += Number(inv.outstanding_amount || 0);
      if (isOverdueInvoice(inv)) acc.overdueAmount += Number(inv.outstanding_amount || 0);
      return acc;
    },
    { totalCredit: 0, totalPaid: 0, totalOutstanding: 0, overdueAmount: 0 }
  );

  const loadPatients = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/patients", { params: searchQuery ? { q: searchQuery } : {} });
      setPatients(data);
    } catch (err) {
      toast.error("Failed to load customer accounts");
    } finally {
      setLoading(false);
    }
  };

  const loadAllCreditInvoices = async () => {
    try {
      const { data } = await api.get("/credit-invoices");
      setAllCreditInvoices(data);
    } catch (err) {
      toast.error("Failed to load credit summary");
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(loadPatients, 250);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    loadAllCreditInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStatementData = async (patientId) => {
    setLoadingLedger(true);
    try {
      const { data } = await api.get(`/patients/${patientId}/ledger`);
      setLedger(data);
    } catch (err) {
      toast.error("Failed to load customer statement");
    } finally {
      setLoadingLedger(false);
    }

    setLoadingInvoices(true);
    try {
      const { data } = await api.get("/credit-invoices", { params: { patient_id: patientId } });
      setInvoices(data);
    } catch (err) {
      toast.error("Failed to load outstanding invoices");
    } finally {
      setLoadingInvoices(false);
    }
  };

  const viewStatement = async (patient) => {
    setSelectedPatient(patient);
    setStatementOpen(true);
    setStatementFromDate("");
    setStatementToDate("");
    await loadStatementData(patient.id);
  };

  const openReceivePayment = async (invoice, patient = null) => {
    setPaymentError("");
    const invoicePatient = patient || patients.find((p) => p.id === invoice?.patient_id) || selectedPatient;
    setPaymentPatient(invoicePatient || null);
    if (invoice) {
      setPaymentInvoices([invoice]);
    } else if (invoicePatient) {
      try {
        const { data } = await api.get("/credit-invoices", { params: { patient_id: invoicePatient.id } });
        const payable = data.filter((inv) => Number(inv.outstanding_amount || 0) > 0);
        setPaymentInvoices(payable);
        invoice = payable[0] || null;
      } catch (err) {
        toast.error("Failed to load payable invoices");
        return;
      }
    }

    if (!invoice) {
      toast.error("No unpaid credit invoice found for this account");
      return;
    }

    setSelectedInvoice(invoice);
    setPaymentAmount(invoice.outstanding_amount);
    setPaymentMode("Cash");
    setPaymentDate(todayISO());
    setPaymentNotes("");
    setPaymentOpen(true);
  };

  const handlePaymentInvoiceChange = (invoiceId) => {
    const invoice = paymentInvoices.find((inv) => inv.id === invoiceId);
    setSelectedInvoice(invoice || null);
    setPaymentAmount(invoice?.outstanding_amount || 0);
    setPaymentError("");
  };

  const handleReceivePayment = async () => {
    if (!selectedInvoice) return;
    setPaymentError("");
    if (paymentAmount <= 0) {
      setPaymentError("Payment amount must be greater than 0");
      return toast.error("Payment amount must be greater than 0");
    }
    if (paymentAmount > selectedInvoice.outstanding_amount) {
      setPaymentError(`Payment cannot exceed outstanding amount (${inr(selectedInvoice.outstanding_amount)})`);
      return toast.error(`Payment cannot exceed outstanding amount (${inr(selectedInvoice.outstanding_amount)})`);
    }

    setSubmittingPayment(true);
    try {
      const payload = {
        credit_invoice_id: selectedInvoice.id,
        amount: Number(paymentAmount),
        payment_mode: paymentMode,
        notes: paymentNotes,
        payment_date: paymentDate,
      };

      await api.post("/credit-payments", payload);
      toast.success("Payment recorded successfully");
      setPaymentOpen(false);

      // Refresh list, ledger, and invoices
      loadPatients();
      loadAllCreditInvoices();
      if (selectedPatient) {
        // Refresh Patient balance
        const { data: updatedPatient } = await api.get("/patients");
        const match = updatedPatient.find(p => p.id === selectedPatient.id);
        if (match) setSelectedPatient(match);

        await loadStatementData(selectedPatient.id);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to receive payment";
      setPaymentError(Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail);
      toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail);
    } finally {
      setSubmittingPayment(false);
    }
  };

  const printStatement = () => {
    const patientName = selectedPatient?.name || "Customer";
    const rows = filteredLedger.map((entry) => `
      <tr>
        <td>${entry.date || ""}</td>
        <td>${entry.reference_no || ""}</td>
        <td>${entry.description || ""}</td>
        <td class="num">${entry.debit_amount > 0 ? inr(entry.debit_amount) : "—"}</td>
        <td class="num">${entry.credit_amount > 0 ? inr(entry.credit_amount) : "—"}</td>
        <td class="num">${inr(entry.balance || 0)}</td>
      </tr>`).join("");
    const invoiceRows = filteredInvoices.map((inv) => `
      <tr class="${isOverdueInvoice(inv) ? "overdue" : ""}">
        <td>${inv.bill_no || ""}</td>
        <td>${inv.credit_date || ""}</td>
        <td>${inv.due_date || ""}</td>
        <td class="num">${inr(inv.total_amount || 0)}</td>
        <td class="num">${inr(inv.paid_amount || 0)}</td>
        <td class="num">${inr(inv.outstanding_amount || 0)}</td>
        <td>${inv.status || ""}</td>
      </tr>`).join("");
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return toast.error("Popup blocked. Please allow popups to print statement.");
    printWindow.document.write(`
      <html><head><title>Credit Statement - ${patientName}</title>
      <style>
        body{font-family:Arial,sans-serif;color:#111827;padding:24px} h1{margin:0 0 4px} .muted{color:#6b7280;font-size:12px} table{width:100%;border-collapse:collapse;margin-top:12px;font-size:12px} th,td{border:1px solid #d1d5db;padding:7px;text-align:left} th{background:#f3f4f6}.num{text-align:right}.overdue{background:#fef2f2;color:#991b1b}.section{margin-top:22px}.balance{float:right;font-weight:700;color:#dc2626}
      </style></head><body>
        <div class="balance">Outstanding: ${inr(selectedPatient?.outstanding_balance || 0)}</div>
        <h1>Credit Account Statement</h1>
        <div class="muted">Customer: ${patientName}</div>
        <div class="muted">Period: ${statementFromDate || "Start"} to ${statementToDate || "Today"}</div>
        <div class="section"><strong>Credit Invoices</strong><table><thead><tr><th>Bill No</th><th>Credit Date</th><th>Due Date</th><th>Total</th><th>Paid</th><th>Outstanding</th><th>Status</th></tr></thead><tbody>${invoiceRows || "<tr><td colspan='7'>No invoices</td></tr>"}</tbody></table></div>
        <div class="section"><strong>Ledger History</strong><table><thead><tr><th>Date</th><th>Ref No</th><th>Description</th><th>Debit</th><th>Credit</th><th>Balance</th></tr></thead><tbody>${rows || "<tr><td colspan='6'>No ledger transactions</td></tr>"}</tbody></table></div>
      </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="space-y-6" data-testid="customer-accounts-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Ledger & Accounts</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Credit Accounts</h1>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search patient…"
            className="pl-9 w-64"
            data-testid="patient-search"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-border bg-white p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Credit</div>
          <div className="mt-1 text-2xl font-bold tabular-nums">{inr(summary.totalCredit)}</div>
        </div>
        <div className="rounded-lg border border-border bg-white p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Paid</div>
          <div className="mt-1 text-2xl font-bold text-green-700 tabular-nums">{inr(summary.totalPaid)}</div>
        </div>
        <div className="rounded-lg border border-border bg-white p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Outstanding</div>
          <div className="mt-1 text-2xl font-bold text-red-600 tabular-nums">{inr(summary.totalOutstanding)}</div>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-red-700">Overdue Amount</div>
          <div className="mt-1 text-2xl font-bold text-red-700 tabular-nums">{inr(summary.overdueAmount)}</div>
        </div>
      </div>

      <div className="rounded-md border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer/Patient</TableHead>
              <TableHead>Contact Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-right">Outstanding Balance</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && patients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Loading accounts...
                </TableCell>
              </TableRow>
            ) : patients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                  <ClipboardList className="mx-auto mb-2 opacity-40" size={24} />
                  No patient accounts found.
                </TableCell>
              </TableRow>
            ) : (
              patients.map((p) => (
                <TableRow key={p.id} data-testid={`patient-account-${p.id}`} className="hover:bg-muted/10">
                  <TableCell className="font-medium text-sm">{p.name}</TableCell>
                  <TableCell className="text-sm font-mono">{p.phone || "—"}</TableCell>
                  <TableCell className="text-sm">{p.email || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    <span className={p.outstanding_balance > 0 ? "text-red-600 font-bold" : "text-muted-foreground"}>
                      {inr(p.outstanding_balance || 0.0)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!p.outstanding_balance || p.outstanding_balance <= 0}
                      onClick={() => openReceivePayment(null, p)}
                      data-testid={`receive-payment-${p.id}`}
                    >
                      <Landmark size={12} className="mr-1.5" /> Receive Payment
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewStatement(p)}
                      data-testid={`view-statement-${p.id}`}
                    >
                      <Eye size={12} className="mr-1.5" /> Statement
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Statement & Ledger View Drawer/Modal */}
      <Dialog open={statementOpen} onOpenChange={setStatementOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="statement-dialog">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center pr-6">
              <span>Ledger Statement: {selectedPatient?.name}</span>
              <span className="text-sm text-red-600 font-bold">
                Outstanding Balance: {inr(selectedPatient?.outstanding_balance || 0.0)}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-muted/20 p-3">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <CalendarDays size={14} /> Statement Filter
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">From</Label>
              <Input type="date" value={statementFromDate} onChange={(e) => setStatementFromDate(e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input type="date" value={statementToDate} onChange={(e) => setStatementToDate(e.target.value)} className="h-8" />
            </div>
            <Button variant="outline" size="sm" onClick={() => { setStatementFromDate(""); setStatementToDate(""); }}>
              Clear
            </Button>
            <Button variant="outline" size="sm" className="ml-auto" onClick={printStatement} data-testid="print-statement-btn">
              <Printer size={14} className="mr-1.5" /> Print Statement
            </Button>
          </div>

          <div className="space-y-6 my-2">
            {/* Outstanding Invoices Section */}
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1.5">
                <Receipt size={14} /> Outstanding / Pending Credit Invoices
              </div>
              <div className="rounded border border-border overflow-hidden">
                <Table>
                  <TableHeader className="bg-muted/30">
                    <TableRow>
                      <TableHead className="py-2">Bill No</TableHead>
                      <TableHead className="py-2">Credit Date</TableHead>
                      <TableHead className="py-2">Due Date</TableHead>
                      <TableHead className="py-2 text-right">Bill Total</TableHead>
                      <TableHead className="py-2 text-right">Paid</TableHead>
                      <TableHead className="py-2 text-right">Outstanding</TableHead>
                      <TableHead className="py-2">Status</TableHead>
                      <TableHead className="py-2 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingInvoices ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-4 text-xs text-muted-foreground">Loading invoices...</TableCell>
                      </TableRow>
                    ) : filteredInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-4 text-xs text-muted-foreground">No outstanding invoices</TableCell>
                      </TableRow>
                    ) : (
                      filteredInvoices.map((inv) => (
                        <TableRow key={inv.id} className={`text-xs hover:bg-muted/10 ${isOverdueInvoice(inv) ? "bg-red-50/80 border-l-4 border-l-red-500" : ""}`}>
                          <TableCell className="font-semibold py-1.5">{inv.bill_no}</TableCell>
                          <TableCell className="py-1.5">{formatDate(inv.credit_date)}</TableCell>
                          <TableCell className={`py-1.5 ${isOverdueInvoice(inv) ? "font-bold text-red-700" : ""}`}>
                            {formatDate(inv.due_date)} {isOverdueInvoice(inv) ? "(Overdue)" : ""}
                          </TableCell>
                          <TableCell className="text-right py-1.5 tabular-nums">{inr(inv.total_amount)}</TableCell>
                          <TableCell className="text-right py-1.5 tabular-nums">{inr(inv.paid_amount)}</TableCell>
                          <TableCell className="text-right py-1.5 tabular-nums font-bold text-red-600">
                            {inr(inv.outstanding_amount)}
                          </TableCell>
                          <TableCell className="py-1.5">
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold border ${inv.status === "Paid"
                                  ? "bg-green-50 text-green-700 border-green-200"
                                  : inv.status === "Partially Paid"
                                    ? "bg-amber-50 text-amber-700 border-amber-200"
                                    : "bg-red-50 text-red-700 border-red-200"
                                }`}
                            >
                              {inv.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-right py-1.5">
                            {inv.outstanding_amount > 0 && (
                              <Button
                                size="xs"
                                onClick={() => openReceivePayment(inv, selectedPatient)}
                                data-testid={`pay-invoice-${inv.bill_no}`}
                              >
                                Pay
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Ledger Transactions Statement */}
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1.5">
                <BookOpen size={14} /> Full Transaction Ledger History
              </div>
              <div className="rounded border border-border overflow-hidden">
                <Table>
                  <TableHeader className="bg-muted/30">
                    <TableRow>
                      <TableHead className="py-2">Date</TableHead>
                      <TableHead className="py-2">Ref No</TableHead>
                      <TableHead className="py-2">Description</TableHead>
                      <TableHead className="py-2 text-right">Debit (+)</TableHead>
                      <TableHead className="py-2 text-right">Credit (-)</TableHead>
                      <TableHead className="py-2 text-right">Running Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingLedger ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4 text-xs text-muted-foreground">Loading ledger...</TableCell>
                      </TableRow>
                    ) : filteredLedger.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4 text-xs text-muted-foreground">No ledger transactions</TableCell>
                      </TableRow>
                    ) : (
                      filteredLedger.map((entry) => (
                        <TableRow key={entry.id} className="text-xs hover:bg-muted/10">
                          <TableCell className="py-1.5">{formatDate(entry.date)}</TableCell>
                          <TableCell className="font-semibold py-1.5">{entry.reference_no}</TableCell>
                          <TableCell className="py-1.5 text-muted-foreground">{entry.description}</TableCell>
                          <TableCell className="text-right py-1.5 tabular-nums text-red-600 font-semibold">
                            {entry.debit_amount > 0 ? `+ ${inr(entry.debit_amount)}` : "—"}
                          </TableCell>
                          <TableCell className="text-right py-1.5 tabular-nums text-green-600 font-semibold">
                            {entry.credit_amount > 0 ? `− ${inr(entry.credit_amount)}` : "—"}
                          </TableCell>
                          <TableCell className="text-right py-1.5 tabular-nums font-bold">
                            {inr(entry.balance)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={printStatement}><Printer size={14} className="mr-1.5" /> Print Statement</Button>
            <Button onClick={() => setStatementOpen(false)}>Close Statement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Payment Modal */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="max-w-md animate-in fade-in" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Receive Payment: {paymentPatient?.name || selectedPatient?.name || "Credit Account"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 my-2 text-sm">
            {paymentInvoices.length > 1 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Invoice *</Label>
                <Select value={selectedInvoice?.id || ""} onValueChange={handlePaymentInvoiceChange}>
                  <SelectTrigger data-testid="pay-invoice-trigger">
                    <SelectValue placeholder="Select invoice" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentInvoices.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>
                        #{inv.bill_no} — {inr(inv.outstanding_amount || 0)} outstanding
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-between p-3 bg-muted/40 rounded border border-border">
              <span className="text-muted-foreground">Invoice #{selectedInvoice?.bill_no} Outstanding:</span>
              <span className="font-bold text-red-600">{inr(selectedInvoice?.outstanding_amount || 0.0)}</span>
            </div>

            {paymentError && (
              <div className="rounded border border-red-200 bg-red-50 p-2 text-xs font-medium text-red-700" data-testid="payment-error">
                {paymentError}
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Amount Received *</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                max={selectedInvoice?.outstanding_amount}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(Math.min(selectedInvoice?.outstanding_amount || 0, Math.max(0, parseFloat(e.target.value) || 0)))}
                data-testid="pay-amount-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Payment Mode *</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger data-testid="pay-mode-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Payment Date</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Notes / Reference No.</Label>
              <Input
                placeholder="e.g. Transaction reference or notes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                data-testid="pay-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentOpen(false)}>Cancel</Button>
            <Button onClick={handleReceivePayment} disabled={submittingPayment} data-testid="submit-payment-btn">
              <Landmark size={14} className="mr-1.5" />
              {submittingPayment ? "Saving..." : "Receive Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
